import type { LucideIcon } from 'lucide-react';
import { BarChart3, ClipboardList, Home, Radio, Settings, UsersRound } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import { useTeam } from '../contexts/TeamContext';
import { useLiveEventCount } from '../hooks/useLiveEventCount';

interface NavItem {
  label: string;
  path: string;
  testId: string;
  icon: LucideIcon;
  showLiveDot?: boolean;
}

const liveItem: NavItem = { label: 'Live', path: '/live', testId: 'nav-item-live', icon: Radio, showLiveDot: true };

const coachItems: NavItem[] = [
  { label: 'Home', path: '/', testId: 'nav-item-home', icon: Home },
  liveItem,
  { label: 'Shooters', path: '/players', testId: 'nav-item-players', icon: UsersRound },
  { label: 'Stats', path: '/team-dashboard', testId: 'nav-item-stats', icon: BarChart3 },
  { label: 'Settings', path: '/settings', testId: 'nav-item-settings', icon: Settings },
];

const memberItems: NavItem[] = [
  { label: 'Home', path: '/', testId: 'nav-item-home', icon: Home },
  liveItem,
  { label: 'Events', path: '/practice/history', testId: 'nav-item-events', icon: ClipboardList },
  { label: 'Settings', path: '/settings', testId: 'nav-item-settings', icon: Settings },
];

export const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeProfile } = useProfile();
  const { currentTeam } = useTeam();
  const liveCount = useLiveEventCount(currentTeam?.id);
  const items = activeProfile?.type === 'coach' ? coachItems : memberItems;

  return (
    <nav
      data-testid="bottom-nav"
      data-bottom-nav
      className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-white safe-bottom"
      aria-label="Primary navigation"
    >
      <div className="mx-auto flex h-[60px] max-w-lg items-stretch">
        {items.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              type="button"
              data-testid={item.testId}
              onClick={() => navigate(item.path)}
              className={`relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 transition-colors ${
                isActive ? 'text-clay-700' : 'text-muted hover:text-ink'
              }`}
              aria-current={isActive ? 'page' : undefined}
              aria-label={item.label}
            >
              <span className="relative">
                <Icon size={23} strokeWidth={isActive ? 2.5 : 2} aria-hidden="true" />
                {item.showLiveDot && liveCount > 0 && (
                  <span data-testid="live-event-indicator" className="absolute -right-1 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-miss" aria-label={`${liveCount} live ${liveCount === 1 ? 'event' : 'events'}`} />
                )}
              </span>
              <span className="max-w-full truncate text-[11px] font-bold leading-tight">{item.label}</span>
              {isActive && <span className="absolute inset-x-4 bottom-0 h-0.5 rounded-t bg-clay-500" />}
            </button>
          );
        })}
      </div>
    </nav>
  );
};
